import { useEffect, useMemo, useRef, useState } from 'react';
import { api, errorText, formatDate, formatQty } from '../api';
import { useAuth } from '../auth';
import { Card, CheckField, Empty, Notice, SelectField, TextField } from '../components/ui';
import type { Article, Location, MovementReason, StockRow } from '../types';

const TABS: { reason: MovementReason; label: string; hint: string }[] = [
  { reason: 'wareneingang', label: 'Wareneingang', hint: 'Ware kommt ins Lager und wird eingelagert.' },
  { reason: 'warenausgang', label: 'Warenausgang', hint: 'Ware verlässt das Lager.' },
  { reason: 'umlagerung', label: 'Umlagerung', hint: 'Ware wechselt den Lagerplatz.' },
  { reason: 'korrektur', label: 'Korrektur', hint: 'Bestandskorrektur gegen den Platz DIFFERENZ.' },
];

/** Lagerplatz per Code - so, wie ihn der Scanner liefert. */
function LocationInput({
  label,
  value,
  onChange,
  locations,
  hint,
}: {
  label: string;
  value: string;
  onChange: (code: string) => void;
  locations: Location[];
  hint?: string;
}) {
  return (
    <label className="field">
      <span>{label}</span>
      <input
        list="location-codes"
        value={value}
        placeholder="Platz scannen oder eintippen"
        onChange={(e) => onChange(e.target.value.toUpperCase())}
      />
      {hint && <span className="small muted">{hint}</span>}
      <datalist id="location-codes">
        {locations.map((location) => (
          <option key={location.id} value={location.code}>
            {location.name ?? location.type}
          </option>
        ))}
      </datalist>
    </label>
  );
}

export function BookingPage() {
  const { owners, ownerId: globalOwner } = useAuth();

  const [reason, setReason] = useState<MovementReason>('wareneingang');
  const [ownerId, setOwnerId] = useState(globalOwner);
  const [term, setTerm] = useState('');
  const [hits, setHits] = useState<Article[]>([]);
  const [article, setArticle] = useState<Article | null>(null);
  const [availability, setAvailability] = useState<StockRow[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);

  const [qty, setQty] = useState('');
  const [fromCode, setFromCode] = useState('');
  const [toCode, setToCode] = useState('');
  const [lotCode, setLotCode] = useState('');
  const [bestBefore, setBestBefore] = useState('');
  const [serial, setSerial] = useState('');
  const [documentRef, setDocumentRef] = useState('');
  const [note, setNote] = useState('');
  const [correctionUp, setCorrectionUp] = useState(true);

  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const scanRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Ohne virtuelle Plätze: DIFFERENZ/EXTERN-WE/EXTERN-WA sind Gegenbuchungs-
    // plätze, die der Server automatisch füllt. Standen sie in der Auswahl,
    // konnte man bei einer Korrektur DIFFERENZ als Platz wählen - dann wurden
    // Quelle und Ziel identisch und die Buchung schlug fehl.
    api<Location[]>('/api/warehouse/locations', { query: { includeVirtual: false } })
      .then(setLocations)
      .catch(() => setLocations([]));
  }, []);

  useEffect(() => setOwnerId(globalOwner), [globalOwner]);

  // Artikelsuche mit kurzer Verzögerung, damit ein Scan nicht mehrfach feuert.
  useEffect(() => {
    if (!term.trim()) {
      setHits([]);
      return;
    }
    const timer = setTimeout(() => {
      api<Article[]>('/api/articles/lookup', {
        query: { q: term.trim(), stockableOnly: 'true' },
      })
        .then((found) => {
          const filtered = ownerId ? found.filter((a) => a.owner_id === ownerId) : found;
          setHits(filtered);
          // Eindeutiger Treffer per Scan: direkt übernehmen.
          if (filtered.length === 1 && (filtered[0]!.ean === term.trim() || filtered[0]!.sku === term.trim())) {
            choose(filtered[0]!);
          }
        })
        .catch(() => setHits([]));
    }, 200);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term, ownerId]);

  function choose(chosen: Article) {
    setArticle(chosen);
    setOwnerId(chosen.owner_id);
    setHits([]);
    setTerm('');
    api<StockRow[]>(`/api/stock/availability/${chosen.id}`, { query: { ownerId: chosen.owner_id } })
      .then(setAvailability)
      .catch(() => setAvailability([]));
  }

  function reset(keepArticle: boolean) {
    setQty('');
    setLotCode('');
    setBestBefore('');
    setSerial('');
    setNote('');
    if (!keepArticle) {
      setArticle(null);
      setAvailability([]);
      setFromCode('');
      setToCode('');
    }
    scanRef.current?.focus();
  }

  const locationByCode = useMemo(() => {
    const map = new Map<string, Location>();
    for (const location of locations) map.set(location.code.toUpperCase(), location);
    return map;
  }, [locations]);

  async function book() {
    setError('');
    setMessage('');

    if (!article) return setError('Bitte zuerst einen Artikel wählen.');
    const amount = Number(qty.replace(',', '.'));
    if (!Number.isFinite(amount) || amount <= 0) return setError('Bitte eine gültige Menge angeben.');

    // Nur die Seite berücksichtigen, die zur Buchungsart/Richtung gehört. Das
    // ausgeblendete Feld behält sonst seinen alten Code: bei einer Abgangs-
    // Korrektur ginge dadurch Quelle UND Ziel mit, und bei gleichem Platz
    // meldete der Server "Quelle und Ziel sind identisch".
    const nutztVon =
      reason === 'warenausgang' || reason === 'umlagerung' || (reason === 'korrektur' && !correctionUp);
    const nutztNach =
      reason === 'wareneingang' || reason === 'umlagerung' || (reason === 'korrektur' && correctionUp);
    const from = nutztVon && fromCode ? locationByCode.get(fromCode.toUpperCase()) : undefined;
    const to = nutztNach && toCode ? locationByCode.get(toCode.toUpperCase()) : undefined;
    if (nutztVon && fromCode && !from) return setError(`Lagerplatz ${fromCode} ist unbekannt.`);
    if (nutztNach && toCode && !to) return setError(`Lagerplatz ${toCode} ist unbekannt.`);

    if (reason === 'wareneingang' && !to) return setError('Ziel-Lagerplatz fehlt.');
    if (reason === 'warenausgang' && !from) return setError('Quell-Lagerplatz fehlt.');
    if (reason === 'umlagerung' && (!from || !to)) return setError('Umlagerung braucht Quelle und Ziel.');
    if (reason === 'korrektur' && !from && !to) return setError('Lagerplatz fehlt.');

    setBusy(true);
    try {
      const result = await api<{ id: string; duplicate: boolean }>('/api/stock/movements', {
        method: 'POST',
        body: {
          // Client-seitige ID: ein doppelt abgesetzter Klick bucht nicht zweimal.
          id: crypto.randomUUID(),
          ownerId: article.owner_id,
          articleId: article.id,
          qty: amount,
          reason,
          fromLocationId: from?.id ?? null,
          toLocationId: to?.id ?? null,
          lotCode: lotCode || null,
          bestBefore: bestBefore || null,
          serial: serial || null,
          documentRef: documentRef || null,
          note: note || null,
        },
      });

      setMessage(
        result.duplicate
          ? 'Diese Buchung war bereits erfasst - es wurde nichts doppelt gebucht.'
          : `Gebucht: ${formatQty(amount, article.unit)} ${article.name}.`,
      );

      const refreshed = await api<StockRow[]>(`/api/stock/availability/${article.id}`, {
        query: { ownerId: article.owner_id },
      });
      setAvailability(refreshed);
      reset(true);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  const tab = TABS.find((t) => t.reason === reason)!;
  const showFrom = reason === 'warenausgang' || reason === 'umlagerung' || (reason === 'korrektur' && !correctionUp);
  const showTo = reason === 'wareneingang' || reason === 'umlagerung' || (reason === 'korrektur' && correctionUp);

  return (
    <>
      <div className="tabs">
        {TABS.map((entry) => (
          <button
            key={entry.reason}
            className={entry.reason === reason ? 'active' : ''}
            onClick={() => {
              setReason(entry.reason);
              setFromCode('');
              setToCode('');
              setError('');
              setMessage('');
            }}
          >
            {entry.label}
          </button>
        ))}
      </div>

      {error && <Notice kind="error">{error}</Notice>}
      {message && <Notice kind="success">{message}</Notice>}

      <div className="grid two">
        <Card title={tab.label}>
          <p className="small muted" style={{ marginTop: 0 }}>
            {tab.hint}
          </p>

          {article ? (
            <div className="chosen">
              <div>
                <strong>{article.name}</strong>
                <div className="small mono">
                  {article.sku}
                  {article.ean ? ` · ${article.ean}` : ''} · {article.owner_name}
                </div>
              </div>
              <span className="spacer" />
              <button className="small" onClick={() => reset(false)}>
                wechseln
              </button>
            </div>
          ) : (
            <>
              {owners.length > 1 && (
                <SelectField
                  label="Eigentümer (optional einschränken)"
                  value={ownerId}
                  onChange={setOwnerId}
                  options={owners}
                  render={(owner) => owner.name}
                  placeholder="alle Eigentümer"
                />
              )}
              <label className="field">
                <span>Artikel scannen oder suchen</span>
                <input
                  ref={scanRef}
                  autoFocus
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                  placeholder="EAN scannen, Artikelnummer oder Bezeichnung"
                />
              </label>
              {hits.length > 0 && (
                <div className="pick-list">
                  {hits.map((hit) => (
                    <button key={hit.id} onClick={() => choose(hit)}>
                      <strong>{hit.name}</strong>
                      <div className="small muted mono">
                        {hit.sku} · {hit.owner_name} · Bestand {formatQty(hit.stock_total, hit.unit)}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {article && (
            <>
              {reason === 'korrektur' && (
                <CheckField
                  label={correctionUp ? 'Richtung: Zugang (Bestand erhöhen)' : 'Richtung: Abgang (Bestand mindern)'}
                  checked={correctionUp}
                  onChange={(v) => {
                    setCorrectionUp(v);
                    // Richtungswechsel: Platzfelder leeren, damit nicht der Platz
                    // der anderen Richtung ungewollt mitgeschickt wird.
                    setFromCode('');
                    setToCode('');
                  }}
                />
              )}

              <div className="form-grid">
                {showFrom && (
                  <LocationInput
                    label="Von Lagerplatz"
                    value={fromCode}
                    onChange={setFromCode}
                    locations={locations}
                  />
                )}
                {showTo && (
                  <LocationInput
                    label="Nach Lagerplatz"
                    value={toCode}
                    onChange={setToCode}
                    locations={locations}
                  />
                )}
                <TextField label={`Menge (${article.unit})`} value={qty} onChange={setQty} />
                {article.requires_lot && (
                  <TextField label="Charge" value={lotCode} onChange={setLotCode} />
                )}
                {article.requires_best_before && (
                  <TextField
                    label="MHD"
                    type="date"
                    value={bestBefore}
                    onChange={setBestBefore}
                  />
                )}
                {article.requires_serial && (
                  <TextField label="Seriennummer" value={serial} onChange={setSerial} />
                )}
                <TextField label="Belegnummer" value={documentRef} onChange={setDocumentRef} />
                <TextField label="Bemerkung" value={note} onChange={setNote} />
              </div>

              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button className="primary" onClick={book} disabled={busy}>
                  {busy ? 'Buchen …' : `${tab.label} buchen`}
                </button>
                <button onClick={() => reset(false)}>Zurücksetzen</button>
              </div>
            </>
          )}
        </Card>

        <Card title="Verfügbarer Bestand" padded={false}>
          {!article ? (
            <Empty>Artikel wählen, um den Bestand zu sehen.</Empty>
          ) : availability.length === 0 ? (
            <Empty>Kein Bestand vorhanden.</Empty>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Lagerplatz</th>
                  <th>Charge</th>
                  <th>MHD</th>
                  <th className="num">Menge</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {availability.map((row) => (
                  <tr key={`${row.location_id}-${row.lot_id ?? 'none'}`}>
                    <td className="mono">{row.location_code}</td>
                    <td>{row.lot_code ?? '—'}</td>
                    <td className="small">{formatDate(row.best_before)}</td>
                    <td className="num">{formatQty(row.qty, row.unit)}</td>
                    <td className="num">
                      {(reason === 'warenausgang' || reason === 'umlagerung') && (
                        <button
                          className="small ghost"
                          onClick={() => {
                            setFromCode(row.location_code);
                            if (row.lot_code) setLotCode(row.lot_code);
                          }}
                        >
                          entnehmen
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    </>
  );
}
